import React, { useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"
import { Helmet } from "react-helmet-async"
import {
  fetchDataSubmissions,
  selectSubmissionsByContext,
  selectSubmissionsByIds,
} from "./slice"
import { parseDataSubmissionFilters } from "./filters"
import { updateSearchParamsWithFilters } from "../filters/filters"
import Layout from "../../ui/Layout"
import NavMain from "../../app/NavMain"
import ScrollablePage from "../../ui/ScrollablePage"
import Paper from "../../ui/Paper"
import DataPageHeader from "../../ui/DataPageHeader"
import ErrorMsg from "../../ui/ErrorMsg"
import LoadingSpinner, { Backdrop } from "../../ui/LoadingSpinner"
import NameIDHeader from "../../ui/NameIDHeader"
import SubmissionsTable from "./SubmissionsTable"

const PAGE_SIZE = 50 // Default initial and subsequent page size

/* SubmissionsList renders a page containing a filter- and sort-able table
 * of all data submission matching the criteria in the current URL.  */
const SubmissionsList = () => {
  const dispatch = useDispatch()

  // we get any matching data submissions for this RSO
  const submissionMatches = useSelector((state) =>
    selectSubmissionsByContext(state, "root")
  )
  // We get the full objects from the ids that match
  const submissions = useSelector((state) =>
    selectSubmissionsByIds(state, submissionMatches?.ids || [])
  )

  // Parse parameters into filter object (which drives filters UI state)
  // Note: We are using the URL as the "state" here, and updates
  // perform a history replacement (so as not to mess up "back" button
  // behaviour too much - though we may want to apply a heuristic like
  // small changes replace history, big changes push history)
  const location = useLocation()
  const navigate = useNavigate()
  const urlParams = new URLSearchParams(location.search)
  const filter = parseDataSubmissionFilters(urlParams)
  updateSearchParamsWithFilters(urlParams, filter)

  useEffect(() => {
    dispatch(
      fetchDataSubmissions({
        filter,
        page: { offset: 0, limit: PAGE_SIZE },
        context: ["root"],
      })
    )
  }, [urlParams.toString()])

  // How we apply a filter update. Filter state is entirely in the URL query parameters
  const setFilter = (filter = {}) => {
    const updUrlParams = updateSearchParamsWithFilters(urlParams, filter)
    navigate({
      pathname: location.pathname,
      search: `?${updUrlParams}`,
    })
  }

  // How many more observations are available to load?
  const loadableCount = Math.min(
    PAGE_SIZE,
    submissions?.length && submissionMatches?.page?.total_count
      ? submissionMatches.page.total_count - submissions.length
      : 50
  )

  // Event handler for 'Show xxx more' button that was clicked
  const handleLoadMore = () => {
    const lastIndex = submissions?.length

    const page = {
      offset: lastIndex,
      limit: loadableCount,
    }
    dispatch(fetchDataSubmissions({ filter, page, context: ["root"] }))
  }

  const addNewSubmission = () => {
    navigate("/data-submissions/selection")
  }

  return (
    <Layout>
      <NavMain key="nav-left" />
      <Helmet>
        <title>Data submissions - Astrosite</title>
      </Helmet>
      <ScrollablePage>
        {submissions.length === 0 &&
          submissionMatches?._loadStatus === "loading" && (
            <Backdrop>
              <LoadingSpinner />
            </Backdrop>
          )}
        <DataPageHeader
          title={<NameIDHeader name={"Data Submissions"} />}
          subtitle={
            <>
              {submissions.length > 0 && (
                <span>
                  Showing{" "}
                  <strong>
                    {submissionMatches?.page?.end_index} of{" "}
                    <strong className="count total_count">
                      {submissionMatches?.page?.total_count}
                    </strong>
                    <span> submissions</span>
                  </strong>
                  {submissionMatches?._loadStatus == "loading" && (
                    <span> updating...</span>
                  )}
                </span>
              )}
              {submissions.length == 0 &&
                submissionMatches?._loadStatus == "loading" && (
                  <span>loading...</span>
                )}
              {submissionMatches?._loadStatus == "rejected" && (
                <ErrorMsg
                  msg={"There was a problem fetching the data submissions"}
                  showIcon={true}
                />
              )}
            </>
          }
        />
        <Paper>
          <button onClick={addNewSubmission}>New submission</button>
          <SubmissionsTable
            submissions={submissions}
            filter={filter}
            setFilter={setFilter}
          />
          {submissions?.length == 0 &&
            submissionMatches?._loadStatus == "idle" && (
              <p>There are no data submissions matching your criteria.</p>
            )}
          <footer className="actions">
            {submissions.length > 0 &&
              submissions.length !== submissionMatches?.page?.total_count && (
                <>
                  <button
                    onClick={handleLoadMore}
                    disabled={submissionMatches?._loadStatus == "loading"}
                  >
                    {submissionMatches?._loadStatus == "loading"
                      ? "Loading..."
                      : `Show ${loadableCount} more`}
                  </button>
                </>
              )}
          </footer>
        </Paper>
      </ScrollablePage>
    </Layout>
  )
}

export default SubmissionsList
